import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { equals, mergeRight, pickBy } from "ramda";
import { isBlank } from "../../../util/ext";

import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";

import EditBar from "../../../components/EditBar";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import SnackAlert from "../../../components/SnackAlert";
import { useSnackAlert } from "../../../common/use-snackAlert";
import { useCollectionCreate } from "../collectionServices";
import { validateCollection } from "../collectionValidation";
import { MediaContext } from "../../common/media-context";
import EditCollectionInfoColumn from "./EditCollectionInfoColumn";

const useStyles = makeStyles(theme => ({
  root: {
    padding: 0,
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    flexWrap: "wrap",
  },

  editContainer: {
    flexGrow: 1,
    paddingBottom: theme.spacing(3),
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: theme.palette.primary.light,
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
  },

  scrollArea: {
    height: "calc(100vh - 300px)",
    overflowY: "auto",
  },

  editPanel: {
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "row",
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },

  commonInfo: {
    flexBasis: "50%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
  },
}));

const objectDiff = media => (val, key) => !equals(media[key], val);

const CollectionNew = props => {
  const { onCancel } = props;
  const classes = useStyles();

  //-- Manage the alert message
  const snackAlertHandler = useSnackAlert();
  const { showAlert } = snackAlertHandler;

  //-- Manage the confirm dialog
  const [confirm, setConfirm] = useState({
    open: false,
    title: "",
    message: "",
    onCancel: () => {},
    onConfirm: () => {},
  });
  const showConfirm = useCallback((title, message, onConfirm, onCancel) => {
    setConfirm(prev =>
      mergeRight(prev, {
        open: true,
        title,
        message,
        onConfirm,
        onCancel,
      }),
    );
  }, []);
  const closeConfirm = useCallback(() => {
    setConfirm(prev =>
      mergeRight(prev, {
        open: false,
        onConfirm: () => {},
        onCancel: () => {},
      }),
    );
  }, []);

  //-- Set up the thumbnail uploader
  // const thumbnailUpload = useUploadCollectionThumbnail();

  const newCollection = {
    mediaType: "collection",
    name: "",
    description: "",
  };

  //-- Set up overall state
  const [collection, setCollection] = useState(newCollection);
  const [initialCollection, setInitialCollection] = useState(newCollection);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [errors, setErrors] = useState({});

  //-- Handle saving the content
  const collectionCreate = useCollectionCreate();
  const handleSave = useCallback(() => {
    setErrors(validateCollection(collection) || {});

    if (isBlank(errors)) {
      collectionCreate.mutate({ data: collection });
    } else {
      showAlert("error", [
        "There are errors that should be fixed before saving.",
      ]);
    }
  }, [errors, collection, collectionCreate, showAlert]);

  useEffect(() => {
    if (!collectionCreate.isIdle && !collectionCreate.isPending) {
      if (collectionCreate.isError) {
        showAlert("error", [collectionCreate.error.message]);
      } else {
        setInitialCollection(collection);
        showAlert("success", ["Collection Successfully Saved"]);
      }
    }
  }, [
    collection,
    collectionCreate.error,
    collectionCreate.isIdle,
    collectionCreate.isPending,
    collectionCreate.isError,
    showAlert,
  ]);

  const handleBackClicked = useCallback(() => {
    const update = pickBy(objectDiff(initialCollection), collection);

    if (isBlank(update)) {
      onCancel();
    } else {
      showConfirm(
        "Discard Changes?",
        "You have made changes to this collection without saving.  Do you wish to discard these changes and go back to the collection list?",
        () => {
          closeConfirm();
          onCancel();
        },
        () => closeConfirm(),
      );
    }
  }, [collection, initialCollection, showConfirm, closeConfirm, onCancel]);

  return (
    <Container
      id="collection-edit"
      maxWidth={false}
      classes={{ root: classes.root }}
    >
      <SnackAlert handler={snackAlertHandler} />

      <MediaContext.Provider
        value={{
          initial: initialCollection,
          media: collection,
          setMedia: setCollection,
          errors: errors,
          setErrors: setErrors,
          thumbnailUrl: thumbnailUrl,
          setThumbnailUrl: setThumbnailUrl,
        }}
      >
        <EditBar onBackClick={handleBackClicked} onSaveClick={handleSave} />
        <Container
          id="collection-new-container"
          maxWidth={false}
          className={classes.editContainer}
        >
          <div className={classes.scrollArea}>
            <Paper id="collection-new-panel" className={classes.editPanel}>
              <EditCollectionInfoColumn />
            </Paper>
          </div>
        </Container>
      </MediaContext.Provider>

      <ConfirmationDialog
        visible={confirm.open}
        title={confirm.title}
        message={confirm.message}
        onConfirm={confirm.onConfirm}
        onCancel={confirm.onCancel}
      />
    </Container>
  );
};

export default CollectionNew;
